import JSZip from 'jszip';
// Use getBlob (SDK) instead of getDownloadURL + fetch: plain fetch hits GCS CORS and
// fails from origins like http://localhost:3000 unless the bucket CORS is customized.
import { ref, getBlob } from 'firebase/storage';
import { uploadedDocumentStorageRefPath } from './parentUploadedDocuments';

function sanitizePathSegment(s) {
  const t = String(s || 'unknown')
    .replace(/[/\\?%*:|"<>]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return t || 'unknown';
}

/**
 * Match a parent portal upload row to a `parents/{id}` document.
 * Prefers uploaderUid / uploaderEmail when present (new uploads).
 */
export function uploadedDocBelongsToParent(doc, parent) {
  if (!doc || !parent?.id) return false;
  if (doc.uploaderUid && doc.uploaderUid === parent.id) return true;
  const docEmail = (doc.uploaderEmail || '').trim().toLowerCase();
  const pEmail = (parent.email || '').trim().toLowerCase();
  if (docEmail && pEmail && docEmail === pEmail) return true;
  const un = (doc.uploaderName || '').trim().toLowerCase();
  if (!un) return false;
  const candidates = [parent.name, parent.displayName]
    .filter(Boolean)
    .map((x) => String(x).trim().toLowerCase())
    .filter(Boolean);
  return candidates.some((c) => c === un);
}

/**
 * @param {import('firebase/storage').FirebaseStorage} storage
 * @param {Array<{ id: string, filename?: string, title?: string, uploaderName?: string }>} rows
 * @param {{ onProgress?: (p: { current: number; total: number }) => void }} options
 */
export async function buildUploadedDocumentsZipBlob(storage, rows, options = {}) {
  const { onProgress } = options;
  const zip = new JSZip();
  const rowsWithFiles = rows.filter((r) => r.filename && uploadedDocumentStorageRefPath(r));
  const total = rowsWithFiles.length;
  if (total === 0) {
    throw new Error('No files with storage paths in this selection.');
  }
  let current = 0;
  for (const row of rowsWithFiles) {
    const path = uploadedDocumentStorageRefPath(row);
    const r = ref(storage, path);
    let blob;
    try {
      blob = await getBlob(r);
    } catch (err) {
      const msg = err?.code === 'storage/unauthorized' ? ' (not signed in or no access)' : '';
      throw new Error(`Could not download "${row.title || row.filename}"${msg}.`);
    }
    const folder = sanitizePathSegment(row.uploaderName);
    const titlePart = sanitizePathSegment(row.title || 'document');
    const orig = row.filename || '';
    const ext = orig.includes('.') ? orig.slice(orig.lastIndexOf('.')) : '';
    const safeExt = ext.length <= 20 ? ext : '';
    const zipPath = `${folder}/${titlePart}_${String(row.id).slice(0, 10)}${safeExt}`;
    zip.file(zipPath, blob);
    current += 1;
    onProgress?.({ current, total });
  }
  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}
